import Link from 'next/link';
import { useMemo, useState } from 'react';

import { getPerpsCoinIconUrl } from '@/lib/hyperliquid/coins';
import { formatUsdPrice } from '@/lib/hyperliquid/prices';
import type { PerpPosition } from '@/lib/hyperliquid/types';

import { DeployerTag } from '@/components/Perps/DeployerTag';
import { getPerpsCoinIconBackgroundColor } from '@/components/Perps/utils';

import { HYPERCORE_CHAIN_ID } from '@/constants/chain';

import { usePerpsCoinLabel } from '@/hooks/perps/usePerpsAnnotations';
import { usePerpsMarkets } from '@/hooks/perps/usePerpsMarketFeed';
import { useFeature } from '@/hooks/useFeature';
import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { fontStyles } from '@/styles/typography';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';

import { classNames } from '@/utils/classnames';
import { PERPS } from '@/utils/featureFlags';
import { routes } from '@/utils/routes';
import type { SortDirection } from '@/utils/zerion/types';

import { SortableHeader } from './Cells/PortfolioHeaderCell';
import { getPnlColorClass, PnlArrow } from './columns';
import { formatTokenBalance } from './utils';

type Props = {
  positions: PerpPosition[];
};

// Same column rhythm as the token table (`PORTFOLIO_GRID_COLS` in PortfolioTable).
const GRID_COLS = 'grid grid-cols-[200px_110px_90px_90px_90px_90px_110px_90px]';

type SortColumn = 'size' | 'pnl' | 'funding' | 'margin';
const HEADERS: { title: string; sortBy?: SortColumn }[] = [
  { title: 'Size', sortBy: 'size' },
  { title: 'Margin', sortBy: 'margin' },
  { title: 'Entry' },
  { title: 'Mark' },
  { title: 'Liq. price' },
  { title: 'PnL', sortBy: 'pnl' },
  { title: 'Funding', sortBy: 'funding' },
];

// cumFunding is positive when the trader has paid; show it from their side.
// `0 -` rather than unary minus so a fresh position reads $0.00, not -$0.00.
const fundingReceived = (position: PerpPosition) => 0 - Number(position.cumFunding.sinceOpen);

const SORT_VALUE: Record<SortColumn, (position: PerpPosition) => number> = {
  size: (position) => Number(position.positionValue),
  pnl: (position) => Number(position.unrealizedPnl),
  funding: fundingReceived,
  margin: (position) => Number(position.marginUsed),
};

const primary = fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-1600' });
const secondary = fontStyles({ variant: 'CAPTION', class: 'text-token-gray-800' });

/** Signed USD amount in the token table's PnL style: colored, arrow for direction. */
function SignedFiat({ value, format }: { value: number; format: (usd: number) => string }) {
  return (
    <span
      className={fontStyles({
        variant: 'BODY_MD',
        class: classNames(getPnlColorClass(value), 'flex items-center gap-1'),
      })}
    >
      {value !== 0 && <PnlArrow isPositive={value > 0} width={5.5} />}
      {format(Math.abs(value))}
    </span>
  );
}

/** Open HyperCore perps positions, streamed by `useHyperCoreAccountFeed`. */
export function HyperCorePositions({ positions }: Props) {
  const label = usePerpsCoinLabel();
  const markets = usePerpsMarkets();
  const { formatFiat } = useFiatDisplay();
  const perpsEnabled = useFeature(PERPS);
  const [sort, setSort] = useState<{ column: SortColumn; direction: SortDirection }>({
    column: 'size',
    direction: 'desc',
  });

  const sorted = useMemo(() => {
    const value = SORT_VALUE[sort.column];
    const sign = sort.direction === 'desc' ? -1 : 1;
    return [...positions].sort((a, b) => sign * (value(a) - value(b)));
  }, [positions, sort]);

  return (
    <div className="min-w-max">
      <div
        className={classNames(
          GRID_COLS,
          'justify-between items-center py-4 pr-5 pl-2',
          'border-b border-token-gray-300',
        )}
      >
        <span className={fontStyles({ variant: 'LABEL_SM', class: 'pl-6 text-token-gray-700' })}>
          Market
        </span>
        {HEADERS.map(({ title, sortBy }) => (
          <SortableHeader
            key={title}
            title={title}
            sortable={sortBy !== undefined}
            sortDirection={sortBy === sort.column ? sort.direction : undefined}
            onSort={() =>
              sortBy &&
              setSort({
                column: sortBy,
                direction: sort.column === sortBy && sort.direction === 'desc' ? 'asc' : 'desc',
              })
            }
          />
        ))}
      </div>

      {sorted.map((position, index) => {
        const coin = position.coin;
        const name = label(coin);
        const size = Number(position.szi);
        const szDecimals = markets[coin]?.szDecimals ?? 0;
        const pnl = Number(position.unrealizedPnl);
        const roe = Number(position.returnOnEquity) * 100;
        const markPx = Number(position.positionValue) / Math.abs(size);
        const funding = fundingReceived(position);
        const market = (
          <div className="flex items-center gap-3 min-w-0 pl-6">
            <TokenAvatar
              chainId={HYPERCORE_CHAIN_ID}
              src={getPerpsCoinIconUrl(coin)}
              size={32}
              className="shrink-0"
              style={{ backgroundColor: getPerpsCoinIconBackgroundColor(coin) }}
            />
            <div className="min-w-0 flex-1">
              <p className={classNames(primary, 'flex items-center gap-1.5')}>
                <span className="truncate">{name}</span>
                <DeployerTag coin={coin} />
              </p>
              <span
                className={fontStyles({
                  variant: 'CAPTION',
                  class: classNames('block truncate', getPnlColorClass(size)),
                })}
              >
                {size > 0 ? 'Long' : 'Short'} {position.leverage.value}x
              </span>
            </div>
          </div>
        );

        return (
          <div
            key={coin}
            className={classNames(
              'relative',
              GRID_COLS,
              'justify-between items-center h-16 pr-5 pl-2',
              'hover:bg-token-alphalight-200 transition-colors',
              'border-token-gray-300',
              index < positions.length - 1 ? 'border-b' : '',
            )}
          >
            {perpsEnabled ? (
              <Link
                href={{ pathname: routes.PERPS, query: { coin: [coin] } }}
                className="absolute inset-0"
                aria-label={`Trade ${name}`}
              />
            ) : null}
            {market}
            <div className="flex flex-col items-start">
              <span className={primary}>{formatFiat(Number(position.positionValue))}</span>
              <span className={secondary}>
                {formatTokenBalance(Math.abs(size))} {name}
              </span>
            </div>
            <div className="flex flex-col items-start">
              <span className={primary}>{formatFiat(Number(position.marginUsed))}</span>
              <span className={secondary}>
                {position.leverage.type === 'isolated' ? 'Isolated' : 'Cross'}
              </span>
            </div>
            <span className={primary}>{formatUsdPrice(position.entryPx, szDecimals)}</span>
            <span className={primary}>{formatUsdPrice(markPx, szDecimals)}</span>
            <span className={primary}>
              {position.liquidationPx ? formatUsdPrice(position.liquidationPx, szDecimals) : '-'}
            </span>
            <div className="flex flex-col items-start">
              <SignedFiat value={pnl} format={formatFiat} />
              <span className={fontStyles({ variant: 'CAPTION', class: getPnlColorClass(pnl) })}>
                {Math.abs(roe).toFixed(2)}%
              </span>
            </div>
            <SignedFiat value={funding} format={formatFiat} />
          </div>
        );
      })}
    </div>
  );
}
